/* WebNest — confirmation dialog (ported from the redesign prototype's feedback.jsx).
   One <NSConfirm> guards every "are you sure?" moment — delete, restore a backup,
   disconnect, discard. `tone` drives the colour + the confirm button; `requireText`
   adds type-to-confirm for high-stakes destructive actions.

   Two ways to use it:
     1. Imperative (preferred for one-off guards):
          import { nsConfirm } from '../shared/ns-confirm'
          if (await nsConfirm({ tone: 'danger', title: 'Delete?', ... })) doIt()
        Mount <NSConfirmHost /> once near the app root (done in App.jsx).
     2. Controlled component: render <NSConfirm {...} onConfirm onCancel /> yourself.

   Leans entirely on existing design tokens (icon-tile tones, drawer-backdrop,
   fadeIn / spin keyframes, btn system) already defined in index.css. */
import React, { useState, useEffect, useRef } from 'react'
import { I, Icon } from './ns-icons'

/* ---- tone → colour + default icon map ---- */
export const FB_TONE = {
  success: { color: 'var(--success)', soft: 'var(--success-soft)', icon: 'check' },
  error:   { color: 'var(--danger)',  soft: 'var(--danger-soft)',  icon: 'x' },
  danger:  { color: 'var(--danger)',  soft: 'var(--danger-soft)',  icon: 'trash' },
  warning: { color: 'var(--warning)', soft: 'var(--warning-soft)', icon: 'shield' },
  info:    { color: 'var(--info)',    soft: 'var(--info-soft)',    icon: 'bell' },
  accent:  { color: 'var(--accent)',  soft: 'var(--accent-soft)',  icon: 'bolt' },
}
/* icon-tile uses class names success/warning/danger/info — map tone → that class */
const FB_TILE = { success: 'success', error: 'danger', danger: 'danger', warning: 'warning', info: 'info', accent: '' }

/* inject the popIn keyframe once (other keyframes already live in index.css) */
function fbInjectStyles() {
  if (typeof document === 'undefined' || document.getElementById('ns-confirm-style')) return
  const el = document.createElement('style')
  el.id = 'ns-confirm-style'
  el.textContent = `@keyframes nsPopIn { from { opacity: 0; transform: translateY(10px) scale(0.96); }
                                          to   { opacity: 1; transform: translateY(0) scale(1); } }`
  document.head.appendChild(el)
}

/* ===================== CONFIRMATION DIALOG ===================== */
export function NSConfirm({
  tone = 'danger',
  icon,
  title = 'Are you sure?',
  message,
  detail,           // optional emphasised callout line (e.g. "This cannot be undone")
  bullets,          // optional array of consequence strings
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  requireText,      // string the user must type to enable confirm (high-stakes deletes)
  onConfirm,
  onCancel,
}) {
  const t = FB_TONE[tone] || FB_TONE.danger
  const [val, setVal] = useState('')
  const [busy, setBusy] = useState(false)
  const ok = !requireText || val.trim() === requireText
  const okRef = useRef(ok)
  okRef.current = ok

  const confirm = async () => {
    if (!okRef.current || busy) return
    setBusy(true)
    try {
      await onConfirm?.()
      // On success the host (or parent) unmounts this dialog; busy stays true
      // through the unmount so the spinner shows for the whole operation.
    } catch (e) {
      setBusy(false) // keep the dialog open so the user can retry
    }
  }

  useEffect(() => {
    fbInjectStyles()
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.()
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) confirm()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, val]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="drawer-backdrop"
      style={{ justifyContent: 'center', alignItems: 'center', zIndex: 400, animation: 'fadeIn 0.16s ease' }}
      onClick={() => !busy && onCancel?.()}
    >
      <div
        className="card"
        style={{ width: 440, maxWidth: '94vw', boxShadow: 'var(--shadow-lg)', animation: 'nsPopIn 0.22s cubic-bezier(0.22,1,0.36,1) both', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
      >
        <div style={{ padding: '22px 22px 0', display: 'flex', gap: 14 }}>
          <span className={`icon-tile ${FB_TILE[tone] || ''}`} style={{ width: 42, height: 42, fontSize: 19, flexShrink: 0, borderRadius: 12 }}>
            <Icon name={icon || t.icon} />
          </span>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.015em', margin: 0 }}>{title}</h2>
            {message && <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55, margin: '7px 0 0', textWrap: 'pretty' }}>{message}</p>}
          </div>
        </div>

        <div style={{ padding: '16px 22px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bullets && bullets.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: '12px 14px', borderRadius: 11, background: 'var(--bg-2)', border: '1px solid var(--border)' }}>
              {bullets.map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--text-soft)', lineHeight: 1.45 }}>
                  <span style={{ color: t.color, flexShrink: 0, marginTop: 1, display: 'flex' }}><I.chevron style={{ fontSize: 13 }} /></span>
                  {b}
                </div>
              ))}
            </div>
          )}

          {detail && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '10px 13px', borderRadius: 10, background: t.soft, color: t.color, fontSize: 12.5, fontWeight: 500 }}>
              <I.shield style={{ flexShrink: 0 }} /> {detail}
            </div>
          )}

          {requireText && (
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 7 }}>
                Type <span className="mono" style={{ color: 'var(--text)', fontWeight: 500 }}>{requireText}</span> to confirm
              </div>
              <input
                className="input mono"
                autoFocus
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder={requireText}
                style={{ borderColor: val && !ok ? 'var(--danger)' : undefined }}
                onKeyDown={(e) => { if (e.key === 'Enter') confirm() }}
              />
            </label>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 22, marginTop: 4 }}>
          <button className="btn" style={{ flex: 1 }} onClick={() => !busy && onCancel?.()} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            onClick={confirm}
            disabled={!ok || busy}
            className="btn"
            style={{
              flex: 1, color: 'white', border: '1px solid transparent',
              background: t.color,
              opacity: !ok ? 0.5 : 1,
              cursor: !ok || busy ? 'not-allowed' : 'pointer',
              boxShadow: ok ? `0 6px 16px -8px ${t.color}` : 'none',
            }}
          >
            {busy
              ? <><span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} /> Working…</>
              : <><Icon name={icon || t.icon} /> {confirmLabel}</>}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ===================== IMPERATIVE API + HOST ===================== */
/* nsConfirm(opts) → Promise<boolean>. Resolves true if confirmed, false if cancelled.
   Pass opts.onConfirm (async) to keep the spinner up while real work runs; otherwise
   the dialog closes immediately on confirm and you do the work afterwards. */
export function nsConfirm(opts = {}) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false)
    window.dispatchEvent(new CustomEvent('ns-confirm', { detail: { opts, _resolve: resolve } }))
  })
}

/* host — mount once near the app root. Renders the active confirmation dialog. */
export function NSConfirmHost() {
  const [req, setReq] = useState(null)
  useEffect(() => {
    const handler = (e) => setReq(e.detail || null)
    window.addEventListener('ns-confirm', handler)
    return () => window.removeEventListener('ns-confirm', handler)
  }, [])

  if (!req) return null
  const { opts, _resolve } = req
  const finish = (result) => { setReq(null); _resolve?.(result) }

  return (
    <NSConfirm
      {...opts}
      onConfirm={async () => { if (opts.onConfirm) await opts.onConfirm(); finish(true) }}
      onCancel={() => finish(false)}
    />
  )
}

/* window parity with the prototype's window.nsToast */
if (typeof window !== 'undefined') {
  window.nsConfirm = nsConfirm
}
